import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'secondary' | 'white';

/**
 * LoadingSpinner Component - Display loading state
 *
 * @example
 * <app-atom-loading-spinner
 *   size="md"
 *   variant="primary"
 *   [overlay]="true"
 *   message="Loading data...">
 * </app-atom-loading-spinner>
 */
@Component({
  selector: 'app-atom-loading-spinner',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './loading-spinner.component.html',
  styleUrls: ['./loading-spinner.component.scss'],
})
export class LoadingSpinnerComponent {
  // Inputs
  size = input<SpinnerSize>('md');
  variant = input<SpinnerVariant>('primary');
  overlay = input<boolean>(false);
  message = input<string>('');
  center = input<boolean>(false);

  spinnerClasses(): string {
    const classes = ['border-4', 'rounded-full', 'animate-spin'];

    // Size mapping
    const sizeMap: Record<SpinnerSize, string> = {
      sm: 'w-6 h-6 border-2',
      md: 'w-10 h-10',
      lg: 'w-16 h-16',
      xl: 'w-24 h-24',
    };

    // Variant mapping
    const variantMap: Record<SpinnerVariant, string> = {
      primary: 'border-primary-200 border-t-primary-600',
      secondary: 'border-gray-200 border-t-gray-600',
      white: 'border-white/30 border-t-white',
    };

    classes.push(sizeMap[this.size()]);
    classes.push(variantMap[this.variant()]);

    return classes.join(' ');
  }

  containerClasses(): string {
    const classes: string[] = [];

    if (this.center()) {
      classes.push('flex', 'flex-col', 'items-center', 'justify-center', 'py-8');
    } else {
      classes.push('inline-flex', 'items-center', 'gap-3');
    }

    return classes.join(' ');
  }

  messageClasses(): string {
    const classes = ['text-gray-700'];

    if (this.center()) {
      classes.push('mt-4', 'text-center');
    }

    return classes.join(' ');
  }
}
